import { useDateFormat } from './useDateFormat'

export interface AgendaEvent {
    id: string
    slug: string
    title: string
    date: string
    fullDate: string // For display: "28 janvier 2026"
    dateRaw: string  // ISO format for technical use (sorting, filters, calendar)
    time: string
    type: string
    description: string
    location: string
    image: string
    // Optional/Missing mapped fields
    registrationLink?: string
    registrationDeadline?: string
    maxRegistrations?: number
    currentRegistrations?: number
    linkedNews?: any[] // Simplified
    requiresRegistration?: boolean
    endDate?: string
    fullEndDate?: string
    dateEndRaw?: string
    endDateFormatted?: string // "07 janv"
    metaTitle?: string
    metaDescription?: string
    keywords?: string
    ogImage?: string
    noIndex?: boolean
}

export const useAgenda = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase
    const { formatDayMonth, formatDayMonthYear, formatFullDate } = useDateFormat()

    const getUpcomingEvents = async (): Promise<AgendaEvent[]> => {
        const { data, error } = await useFetch<AgendaEvent[]>(`${apiBase}/oxoweb/events`)
        if (error.value) {
            console.error('Error fetching events:', error.value)
            return []
        }

        return (data.value || []).map(e => {
            return {
                id: e.slug,
                slug: e.slug,
                title: e.title,
                time: e.time,
                type: e.type,
                description: e.description || '',
                location: e.location,
                image: e.image,
                dateRaw: (e as any).date,
                dateEndRaw: (e as any).endDate,
                fullDate: formatFullDate((e as any).date),
                fullEndDate: (e as any).endDate ? formatFullDate((e as any).endDate) : undefined,
                date: formatDayMonth((e as any).date),
                endDateFormatted: (e as any).endDate ? formatDayMonth((e as any).endDate) : undefined
            }
        })
    }

    const getEventTypes = (events: AgendaEvent[]) => ['Tous', ...new Set(events.map(e => e.type))]

    const getWeekRange = () => {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const nextWeek = new Date(today)
        nextWeek.setDate(today.getDate() + 7)
        return { today, nextWeek }
    }

    const filterEventsThisWeek = (events: AgendaEvent[]) => {
        const { today, nextWeek } = getWeekRange()
        return events.filter(e => {
            const d = new Date(e.dateRaw)
            return d >= today && d < nextWeek
        })
    }

    const filterEventsSoon = (events: AgendaEvent[]) => {
        const { nextWeek } = getWeekRange()
        return events.filter(e => new Date(e.dateRaw) >= nextWeek)
    }

    const getEventBySlug = async (slug: string): Promise<AgendaEvent | undefined> => {
        const { data, error } = await useFetch(`${apiBase}/oxoweb/events/${slug}`)
        if (error.value) {
            console.error(`Error fetching event ${slug}:`, error.value)
            return undefined
        }
        const e = data.value as any
        if (!e) return undefined

        return {
            id: e.slug,
            slug: e.slug,
            title: e.title,
            time: e.time,
            type: e.type,
            description: e.description,
            location: e.location,
            image: e.image,
            dateRaw: e.date,
            dateEndRaw: e.endDate,
            fullDate: formatFullDate(e.date), // SSR-safe formatted date
            fullEndDate: e.endDate ? formatFullDate(e.endDate) : undefined,
            date: formatDayMonth(e.date), // SSR-safe
            currentRegistrations: e.registrations,
            requiresRegistration: e.requiresRegistration,
            registrationDeadline: e.registrationDeadline,
            maxRegistrations: e.maxRegistrations,
            linkedNews: (e.linkedNews || []).map((n: any) => ({
                ...n,
                date: formatDayMonthYear(n.date)
            }))
        }
    }

    return {
        getUpcomingEvents,
        getEventTypes,
        filterEventsThisWeek,
        filterEventsSoon,
        getEventBySlug
    }
}
